import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  ScanLine, CheckCircle, XCircle, AlertCircle,
  User, CreditCard, Camera, CameraOff, MapPin,
  Package, Truck, ArrowRight, Upload, Phone
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { parseQrCode } from '../../lib/qrCode';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ScannerPage = () => {
  const { token } = useAuth();
  const [manualRef, setManualRef] = useState('');
  const [scanResult, setScanResult] = useState(null); // { type: 'booking'|'luggage', data: ..., message: ... }
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);

  // Luggage pay-forward collection in scanner
  const [collectAmount, setCollectAmount] = useState('');
  const [collectCurrency, setCollectCurrency] = useState('USD');
  const [showCollectBox, setShowCollectBox] = useState(false);
  const [collectorName, setCollectorName] = useState('');
  const [collectorIdNumber, setCollectorIdNumber] = useState('');

  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const fileInputRef = useRef(null);
  const scanLockRef = useRef(false);

  const startCamera = async () => {
    setCameraError(null);
    if (!window.isSecureContext) {
      setCameraError('Camera access requires HTTPS. Open the secure site URL or use localhost.');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('This browser does not support camera access. Try a current version of Chrome, Safari, or Firefox.');
      return;
    }
    setCameraActive(true);
    scanLockRef.current = false;
    // Let React render the visible div first
    await new Promise((r) => setTimeout(r, 150));

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      if (html5QrRef.current) {
        try { await html5QrRef.current.stop(); } catch (_) {}
      }
      const scanner = new Html5Qrcode('qr-reader');
      html5QrRef.current = scanner;

      const onScanSuccess = async (decodedText) => {
        if (scanLockRef.current) return;
        scanLockRef.current = true;
        const decoded = parseQrCode(decodedText);
        if (!decoded.ref) {
          scanLockRef.current = false;
          return;
        }
        try {
          await stopCamera();
          await processCode(decoded.ref, decoded.type);
        } finally {
          scanLockRef.current = false;
        }
      };

      // Try back camera first, fallback to user camera, then any
      const scanConfig = { fps: 10, qrbox: { width: 250, height: 250 } };
      try {
        await scanner.start({ facingMode: 'environment' }, scanConfig, onScanSuccess, () => {});
      } catch (backErr) {
        try {
          await scanner.start({ facingMode: 'user' }, scanConfig, onScanSuccess, () => {});
        } catch (userErr) {
          const cameras = await Html5Qrcode.getCameras();
          const cameraId = cameras?.find(camera => typeof camera?.id === 'string' && camera.id.trim())?.id;
          if (!cameraId) throw new Error('No usable camera devices found on this system');
          await scanner.start(cameraId, scanConfig, onScanSuccess, () => {});
        }
      }
    } catch (err) {
      console.error('Camera start error:', err);
      setCameraActive(false);
      const cameraErrors = {
        NotAllowedError: 'Camera permission was denied. Allow camera access for this site in your browser settings.',
        PermissionDeniedError: 'Camera permission was denied. Allow camera access for this site in your browser settings.',
        NotFoundError: 'No camera was found on this device.',
        NotReadableError: 'The camera is already in use by another app or tab. Close it and try again.',
        OverconstrainedError: 'The requested camera is unavailable. Check your device camera settings and try again.',
      };
      setCameraError(cameraErrors[err.name] || err.message || 'Could not access the camera. Check browser permissions or enter the reference manually.');
    }
  };

  const stopCamera = async () => {
    if (html5QrRef.current) {
      try {
        if (html5QrRef.current.isScanning) {
          await html5QrRef.current.stop();
        }
      } catch (_) {}
      try { html5QrRef.current.clear(); } catch (_) {}
      html5QrRef.current = null;
    }
    setCameraActive(false);
  };

  // Scan from photo / image file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('qr-file-helper');
      let decodedText;
      try {
        decodedText = await scanner.scanFile(file, false);
      } catch (decodeError) {
        if (!('BarcodeDetector' in window) || typeof createImageBitmap !== 'function') {
          throw decodeError;
        }

        const bitmap = await createImageBitmap(file);
        try {
          const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          const results = await detector.detect(bitmap);
          decodedText = results[0]?.rawValue;
        } finally {
          bitmap.close();
        }
        if (!decodedText) throw decodeError;
      }
      const decoded = parseQrCode(decodedText);
      if (!decoded.ref) throw new Error('The QR code was read, but it does not contain a ticket or luggage reference.');
      await processCode(decoded.ref, decoded.type);
    } catch (err) {
      console.warn('Uploaded QR scan failed:', err);
      const errorMessage = err?.message || String(err || '');
      if (errorMessage.includes('does not contain a ticket or luggage reference')) {
        toast.error(errorMessage);
      } else {
        toast.error('Could not read a QR code from this image. Try a clear, well-lit JPG or PNG with the full code visible.');
      }
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    return () => { stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualEntry = async () => {
    if (!manualRef.trim()) {
      toast.error('Please enter a booking or luggage reference');
      return;
    }
    const decoded = parseQrCode(manualRef);
    if (!decoded.ref) {
      toast.error('The scanned code does not contain a booking or luggage reference');
      return;
    }
    await processCode(decoded.ref, decoded.type);
  };

  // Universal lookup: detects Passenger Booking or Luggage item
  const processCode = async (ref, codeType = null) => {
    setLoading(true);
    setScanResult(null);
    setShowCollectBox(false);
    setCollectorName('');
    setCollectorIdNumber('');

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // 1. If starts with LUG or LP, or looks like luggage, try luggage first
    if (codeType === 'luggage' || ref.startsWith('LUG') || ref.startsWith('LP')) {
      try {
        const res = await axios.get(`${API_URL}/luggage/${ref}`, { headers });
        setScanResult({ type: 'luggage', data: res.data, message: 'Luggage item found' });
        setCollectorName(res.data.receiver_name || '');
        setLoading(false);
        return;
      } catch (_) {}
    }

    // 2. Try passenger booking
    try {
      const res = await axios.get(`${API_URL}/bookings/${ref}`, { headers });
      setScanResult({ type: 'booking', data: res.data, message: 'Passenger booking found' });
      setLoading(false);
      return;
    } catch (_) {}

    // 3. Fallback: try luggage if not tried
    try {
      const res = await axios.get(`${API_URL}/luggage/${ref}`, { headers });
      setScanResult({ type: 'luggage', data: res.data, message: 'Luggage item found' });
      setCollectorName(res.data.receiver_name || '');
      setLoading(false);
      return;
    } catch (_) {}

    setScanResult({
      success: false,
      message: `No booking or luggage found matching: ${ref}`
    });
    setLoading(false);
  };

  // Passenger Check-in & Board action
  const handleCheckInPassenger = async () => {
    if (!scanResult?.data?.booking_ref) return;
    setActionSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/bookings/${scanResult.data.booking_ref}/checkin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.already_checked_in) {
        toast.info('Passenger was already checked in.');
      } else {
        toast.success('✅ Passenger checked in and boarded on bus!');
      }
      setScanResult(prev => ({
        ...prev,
        data: { ...prev.data, checked_in: true, boarded: true, status: 'checked_in' },
        checkedIn: true
      }));
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('⛔ Not your trip — you can only check in passengers on your assigned trip.');
      } else {
        toast.error(error.response?.data?.detail || 'Check-in failed');
      }
    } finally {
      setActionSubmitting(false);
    }
  };

  // Luggage Status Update action (loaded, in_transit, arrived, claimed/delivered)
  const handleUpdateLuggageStatus = async (newStatus) => {
    if (!scanResult?.data?.luggage_ref) return;
    if (newStatus === 'claimed' && !collectorName.trim()) {
      toast.error('Enter the name of the person collecting the luggage');
      return;
    }
    setActionSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/luggage/${scanResult.data.luggage_ref}/scan`,
        {
          new_status: newStatus,
          ...(newStatus === 'claimed' ? {
            collected_by_name: collectorName.trim(),
            collected_by_id_number: collectorIdNumber.trim() || null
          } : {})
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ Luggage marked as ${newStatus.toUpperCase()}!`);
      // Refresh luggage details
      const refreshed = await axios.get(`${API_URL}/luggage/${scanResult.data.luggage_ref}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setScanResult(prev => ({ ...prev, data: refreshed.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to update luggage status to ${newStatus}`);
    } finally {
      setActionSubmitting(false);
    }
  };

  // Luggage Pay-Forward Collect action
  const handleCollectLuggageBalance = async () => {
    if (!scanResult?.data?.luggage_ref) return;
    const amount = parseFloat(collectAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    setActionSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/luggage/${scanResult.data.luggage_ref}/collect-balance`,
        { amount, currency: collectCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('✅ Pay-forward balance updated and verified!');
      const refreshed = await axios.get(`${API_URL}/luggage/${scanResult.data.luggage_ref}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setScanResult(prev => ({ ...prev, data: refreshed.data }));
      setShowCollectBox(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record pay-forward balance');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleClear = () => {
    setScanResult(null);
    setManualRef('');
    setShowCollectBox(false);
  };

  // Data helpers
  const isBooking = scanResult?.type === 'booking';
  const isLuggage = scanResult?.type === 'luggage';
  const booking = isBooking ? scanResult.data : null;
  const luggage = isLuggage ? scanResult.data : null;

  const isZeroFare = booking && (booking.total_amount || 0) <= 0;
  const isPaymentOk = booking && (
    booking.payment_status === 'paid' ||
    isZeroFare ||
    (booking.status === 'confirmed' && booking.payment_status === 'partial')
  );
  const alreadyCheckedIn = booking?.checked_in || scanResult?.checkedIn;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">QR Code Scanner</h1>
          <p className="text-slate-600">Scan passenger tickets or luggage tags to perform boarding &amp; delivery tasks</p>
        </div>

        {/* Hidden element for file scanning */}
        <div id="qr-file-helper" style={{ display: 'none' }} />

        {/* Scanner Card */}
        <Card className="card-panashe">
          <CardHeader>
            <CardTitle className="font-heading flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ScanLine className="h-5 w-5 text-panashe-blue" />
                Live Camera Scanner
              </span>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs"
                >
                  <Upload className="h-3.5 w-3.5 mr-1" />
                  Scan from Image
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Camera Area */}
            <div className="rounded-lg overflow-hidden bg-slate-900 min-h-[220px] flex items-center justify-center relative">
              {!cameraActive && (
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <Camera className="h-16 w-16 text-slate-500 mb-2" />
                  <p className="text-slate-300 text-sm font-medium">Camera is inactive</p>
                  <p className="text-slate-500 text-xs mt-1 max-w-xs">
                    {cameraError || 'Click "Start Camera" to scan tickets or parcels with your device camera.'}
                  </p>
                </div>
              )}
              {/* Dedicated scanner container - React never adds children here */}
              <div
                id="qr-reader"
                ref={scannerRef}
                style={{ width: '100%', display: cameraActive ? 'block' : 'none' }}
              />
            </div>

            {/* Camera Controls */}
            {cameraActive ? (
              <Button onClick={stopCamera} variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50">
                <CameraOff className="h-4 w-4 mr-2" /> Stop Camera
              </Button>
            ) : (
              <Button onClick={startCamera} className="w-full btn-panashe" disabled={loading}>
                <Camera className="h-4 w-4 mr-2" /> Start Camera (Scan QR)
              </Button>
            )}

            {/* Manual Lookup */}
            <div className="flex gap-2 pt-2 border-t">
              <Input
                placeholder="Enter Booking Ref (PAN...) or Luggage Ref (LUG...)"
                value={manualRef}
                onChange={(e) => setManualRef(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleManualEntry()}
                className="font-mono text-sm"
                data-testid="manual-ref-input"
              />
              <Button
                onClick={handleManualEntry}
                disabled={loading}
                variant="outline"
                className="border-panashe-blue text-panashe-blue hover:bg-panashe-blue hover:text-white"
                data-testid="lookup-btn"
              >
                {loading ? 'Searching...' : 'Look Up'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Result Display */}
        {scanResult && (
          <Card className={`card-panashe overflow-hidden border-2 ${
            scanResult.success === false
              ? 'border-red-500'
              : isBooking
              ? alreadyCheckedIn ? 'border-green-500' : 'border-panashe-blue'
              : 'border-purple-500'
          }`}>
            {/* Header */}
            <div className={`p-4 text-white flex items-center justify-between ${
              scanResult.success === false
                ? 'bg-red-500'
                : isBooking
                ? alreadyCheckedIn ? 'bg-green-600' : 'bg-panashe-blue'
                : 'bg-purple-700'
            }`}>
              <div className="flex items-center gap-2">
                {scanResult.success === false ? (
                  <XCircle className="h-6 w-6" />
                ) : isBooking ? (
                  alreadyCheckedIn ? <CheckCircle className="h-6 w-6" /> : <ScanLine className="h-6 w-6" />
                ) : (
                  <Package className="h-6 w-6" />
                )}
                <div>
                  <span className="font-heading text-base md:text-lg font-bold block">
                    {scanResult.success === false
                      ? scanResult.message
                      : isBooking
                      ? alreadyCheckedIn ? 'Passenger Checked In & Boarded ✓' : 'Passenger Ticket Verified'
                      : `Luggage Parcel Verified (${(luggage?.status || '').toUpperCase()})`}
                  </span>
                  <span className="text-xs text-white/80">
                    {isBooking ? 'Passenger Transit System' : 'Luggage & Cargo System'}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={handleClear}>
                Clear
              </Button>
            </div>

            {/* ==================== PASSENGER BOOKING VIEW ==================== */}
            {isBooking && booking && (
              <CardContent className="p-6 space-y-4">
                <div className="text-center pb-4 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Booking Reference</p>
                  <p className="font-mono text-2xl font-bold text-panashe-blue">{booking.booking_ref}</p>
                  {isZeroFare && (
                    <Badge className="mt-1 bg-purple-100 text-purple-700">🎁 Complimentary / Free Ticket</Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Route</p>
                    <p className="font-medium">{booking.origin || booking.trip_details?.origin} → {booking.destination || booking.trip_details?.destination}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Seats</p>
                    <p className="font-medium font-mono text-base text-panashe-red">{booking.seat_numbers?.join(', ')}</p>
                  </div>
                </div>

                {/* Passenger list */}
                <div>
                  <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Passengers on Ticket</p>
                  <div className="space-y-2">
                    {booking.passengers?.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg border">
                        <div className="w-8 h-8 bg-panashe-blue rounded-full flex items-center justify-center text-white flex-shrink-0">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{p.full_name}</p>
                          <p className="text-xs text-slate-500">
                            {p.passport_number ? `🛂 Passport: ${p.passport_number}` : p.is_local ? '🏠 Local Passenger' : ''}
                            {p.phone ? ` • 📞 ${p.phone}` : ''}
                          </p>
                        </div>
                        <Badge variant="outline" className="font-mono">Seat {booking.seat_numbers?.[idx]}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment & Balance */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-slate-400" />
                    <span className="text-slate-700 text-sm font-medium">Payment Status</span>
                  </div>
                  <Badge className={
                    booking.payment_status === 'paid' ? 'bg-green-100 text-green-700'
                    : booking.payment_status === 'partial' ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                  }>
                    {isZeroFare ? 'EXEMPT / FREE' : booking.payment_status?.toUpperCase()}
                  </Badge>
                </div>

                {(booking.pay_forward_balance || 0) > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm font-medium text-amber-900">
                      Pay-Forward Balance: {booking.currency === 'ZAR' ? 'R' : '$'}{Number(booking.pay_forward_balance).toFixed(2)} due at destination
                    </p>
                  </div>
                )}

                {/* Action: Check-in & Board */}
                {!alreadyCheckedIn && isPaymentOk && (
                  <Button
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base h-12 shadow"
                    onClick={handleCheckInPassenger}
                    disabled={actionSubmitting}
                    data-testid="checkin-btn"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {actionSubmitting ? 'Checking in...' : 'Confirm Check-in & Board Passenger'}
                  </Button>
                )}

                {alreadyCheckedIn && (
                  <div className="flex items-center justify-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 font-semibold text-sm">
                    <CheckCircle className="h-5 w-5" />
                    <span>Passenger Checked In &amp; Boarded on Bus ✓</span>
                  </div>
                )}

                {!isPaymentOk && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Cash payment must be approved before checking in.</span>
                  </div>
                )}

                <Button
                  variant="outline"
                  className="w-full border-panashe-blue text-panashe-blue hover:bg-blue-50 text-sm"
                  onClick={() => window.open(`/ticket/${booking.booking_ref}`, '_blank')}
                >
                  <MapPin className="h-4 w-4 mr-2" /> View / Print Passenger Ticket
                </Button>
              </CardContent>
            )}

            {/* ==================== LUGGAGE PARCEL VIEW ==================== */}
            {isLuggage && luggage && (
              <CardContent className="p-6 space-y-4">
                <div className="text-center pb-4 border-b">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Luggage Reference</p>
                  <p className="font-mono text-2xl font-bold text-purple-800">{luggage.luggage_ref}</p>
                  <Badge className="mt-1 bg-purple-100 text-purple-800 border-purple-200 font-semibold">
                    Status: {(luggage.status || '').toUpperCase()}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-lg border">
                  <div>
                    <p className="text-xs text-slate-500">Item Description</p>
                    <p className="font-medium text-slate-800">{luggage.description} ({luggage.quantity} item{luggage.quantity > 1 ? 's' : ''})</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Declared Value</p>
                    <p className="font-medium text-slate-800">{luggage.declared_value_currency || 'ZAR'} {luggage.declared_value || '0'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Sender</p>
                    <p className="font-medium text-slate-800">{luggage.sender_name} ({luggage.sender_phone})</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Receiver</p>
                    <p className="font-medium text-slate-800">{luggage.receiver_name} ({luggage.receiver_phone})</p>
                  </div>
                  {luggage.pickup_point && (
                    <div>
                      <p className="text-xs text-slate-500">Pick-up Point</p>
                      <p className="font-medium text-purple-700">📍 {luggage.pickup_point}</p>
                    </div>
                  )}
                  {luggage.dropoff_point && (
                    <div>
                      <p className="text-xs text-slate-500">Drop-off Point</p>
                      <p className="font-medium text-purple-700">📍 {luggage.dropoff_point}</p>
                    </div>
                  )}
                </div>

                {/* Pay forward balance notification */}
                {(luggage.pay_forward_balance || 0) > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold text-amber-900">
                            Pay-Forward Due: {luggage.currency === 'ZAR' ? 'R' : '$'}{Number(luggage.pay_forward_balance).toFixed(2)}
                          </p>
                          <p className="text-xs text-amber-700">
                            Cannot release luggage until this balance is paid.
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                        onClick={() => setShowCollectBox(!showCollectBox)}
                      >
                        Collect Balance
                      </Button>
                    </div>

                    {showCollectBox && (
                      <div className="pt-3 border-t border-amber-200 flex gap-2 items-center">
                        <Input
                          type="number"
                          placeholder="Amount"
                          value={collectAmount}
                          onChange={(e) => setCollectAmount(e.target.value)}
                          className="bg-white text-sm h-9 w-28"
                        />
                        <select
                          value={collectCurrency}
                          onChange={(e) => setCollectCurrency(e.target.value)}
                          className="bg-white border rounded px-2 text-sm h-9"
                        >
                          <option value="USD">USD</option>
                          <option value="ZAR">ZAR</option>
                        </select>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-9"
                          onClick={handleCollectLuggageBalance}
                          disabled={actionSubmitting}
                        >
                          Confirm Paid
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Luggage Workflow Action Buttons */}
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Luggage Actions</p>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {luggage.status === 'registered' && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm col-span-2"
                        onClick={() => handleUpdateLuggageStatus('checked_in')}
                        disabled={actionSubmitting}
                      >
                        <CheckCircle className="h-4 w-4 mr-1.5" /> Check In Luggage
                      </Button>
                    )}

                    {(luggage.status === 'checked_in' || luggage.status === 'accepted') && (
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm col-span-2"
                        onClick={() => handleUpdateLuggageStatus('loaded')}
                        disabled={actionSubmitting}
                      >
                        <Truck className="h-4 w-4 mr-1.5" /> Mark Loaded on Bus
                      </Button>
                    )}

                    {luggage.status === 'loaded' && (
                      <Button
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm col-span-2"
                        onClick={() => handleUpdateLuggageStatus('in_transit')}
                        disabled={actionSubmitting}
                      >
                        <Truck className="h-4 w-4 mr-1.5" /> Mark In Transit
                      </Button>
                    )}

                    {luggage.status === 'in_transit' && (
                      <Button
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm col-span-2"
                        onClick={() => handleUpdateLuggageStatus('offloaded')}
                        disabled={actionSubmitting}
                      >
                        <MapPin className="h-4 w-4 mr-1.5" /> Offload at Destination
                      </Button>
                    )}

                    {(luggage.status === 'offloaded' || luggage.status === 'arrived') && (
                      <div className="col-span-2 space-y-2">
                        <Input
                          value={collectorName}
                          onChange={(event) => setCollectorName(event.target.value)}
                          placeholder="Name of person collecting"
                          aria-label="Name of person collecting luggage"
                        />
                        <Input
                          value={collectorIdNumber}
                          onChange={(event) => setCollectorIdNumber(event.target.value)}
                          placeholder="Collector ID / passport number (optional)"
                          aria-label="Collector ID or passport number"
                        />
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold text-sm"
                          onClick={() => handleUpdateLuggageStatus('claimed')}
                          disabled={actionSubmitting || !collectorName.trim()}
                        >
                          <CheckCircle className="h-4 w-4 mr-1.5" /> Deliver / Release to Receiver
                        </Button>
                      </div>
                    )}

                    {luggage.status === 'claimed' && (
                      <div className="col-span-2 p-3 bg-green-50 border border-green-200 rounded-lg text-center text-green-800 font-semibold text-sm">
                        ✅ Parcel Delivered and Handed Over to Receiver
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2 border-t pt-4">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Tracking History</p>
                  {luggage.events?.length ? (
                    <ol className="space-y-2 max-h-56 overflow-y-auto">
                      {[...luggage.events].reverse().map((event, index) => (
                        <li key={`${event.timestamp || event.status}-${index}`} className="rounded border bg-slate-50 p-3 text-sm">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold capitalize">{String(event.status || 'update').replaceAll('_', ' ')}</span>
                            <time className="text-xs text-slate-500">
                              {event.timestamp ? new Date(event.timestamp).toLocaleString() : 'Time unavailable'}
                            </time>
                          </div>
                          {(event.user_name || event.station_name) && (
                            <p className="mt-1 text-xs text-slate-600">{[event.user_name, event.station_name].filter(Boolean).join(' · ')}</p>
                          )}
                          {(event.collected_by_name || event.notes) && (
                            <p className="mt-1 text-xs text-slate-600">
                              {event.collected_by_name ? `Collected by ${event.collected_by_name}` : event.notes}
                            </p>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-slate-500">No tracking events recorded yet.</p>
                  )}
                </div>

                <Button
                  variant="outline"
                  className="w-full border-purple-400 text-purple-700 hover:bg-purple-50 text-sm"
                  onClick={() => window.open(`/luggage-ticket/${luggage.luggage_ref}`, '_blank')}
                >
                  <Package className="h-4 w-4 mr-2" /> View / Print Luggage Receipt
                </Button>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ScannerPage;
